// Story 10.4 — Card reusable para una KPI con valor + delta opcional.

import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { formatDelta } from '@/lib/period-presets';
import { cn } from '@/lib/utils';

interface Props {
  label: string;
  value: number | string;
  /** Diff absoluto vs periodo anterior. Si pasas también `previous`, se muestra %. */
  delta?: number;
  /** Valor del periodo previo, requerido si quieres porcentaje. */
  previous?: number;
  icon?: React.ReactNode;
  loading?: boolean;
}

export function KpiCard({
  label,
  value,
  delta,
  previous,
  icon,
  loading,
}: Props): React.ReactElement {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="h-3 w-24 animate-pulse rounded bg-muted" />
        </CardHeader>
        <CardContent>
          <div className="h-8 w-16 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
    );
  }

  let deltaDisplay: ReturnType<typeof formatDelta> | null = null;
  if (delta !== undefined && previous !== undefined) {
    deltaDisplay = formatDelta(delta, previous);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {icon && <div className="text-muted-foreground">{icon}</div>}
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tracking-tight">{value}</p>
        {deltaDisplay && (
          <p
            className={cn(
              'mt-1 inline-flex items-center gap-1 text-xs',
              deltaDisplay.trend === 'up' && 'text-success',
              deltaDisplay.trend === 'down' && 'text-destructive',
              deltaDisplay.trend === 'neutral' && 'text-muted-foreground',
            )}
          >
            {deltaDisplay.trend === 'up' && <ArrowUp className="h-3 w-3" aria-hidden />}
            {deltaDisplay.trend === 'down' && <ArrowDown className="h-3 w-3" aria-hidden />}
            {deltaDisplay.trend === 'neutral' && <Minus className="h-3 w-3" aria-hidden />}
            {deltaDisplay.text} <span className="text-muted-foreground">vs anterior</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
